/**
 * 快速添加任务弹窗：仅收集常用字段，请求体严格按 CreateTaskBody 白名单拼装（不带 undefined 多余字段）。
 */
import { useEffect, useState } from 'react';
import { useAreas, useCreateTask } from '../hooks/useTasks';
import type { CreateTaskBody, Priority } from '../lib/types';
import { PRIORITY_LABELS, PRIORITY_OPTIONS, todayStr } from '../lib/utils';
import { Button, ErrorBox, Field, Input, Modal, Select, Textarea, errMsg } from './ui';

interface QuickAddProps {
  open: boolean;
  onClose: () => void;
  defaults?: { dueDate?: string; areaId?: string; projectId?: string; contactId?: string };
}

const emptyForm = { title: '', notes: '', dueDate: '', dueTime: '', priority: '', areaId: '' };

export function QuickAdd({ open, onClose, defaults }: QuickAddProps) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { data: areas } = useAreas();
  const createTask = useCreateTask();

  // 打开时重置表单并套用默认值
  useEffect(() => {
    if (open) {
      setForm({
        ...emptyForm,
        dueDate: defaults?.dueDate ?? todayStr(),
        areaId: defaults?.areaId ?? '',
      });
      setError(null);
    }
  }, [open, defaults?.dueDate, defaults?.areaId]);

  const set = (patch: Partial<typeof emptyForm>) => setForm((f) => ({ ...f, ...patch }));

  async function submit() {
    if (!form.title.trim()) {
      setError('请填写任务标题');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateTaskBody = { title: form.title.trim() };
      if (form.notes.trim()) body.notes = form.notes.trim();
      if (form.dueDate) body.dueDate = form.dueDate;
      if (form.dueTime) body.dueTime = form.dueTime;
      if (form.priority) body.priority = Number(form.priority) as Priority;
      if (form.areaId) body.areaId = form.areaId;
      if (defaults?.projectId) body.projectId = defaults.projectId;
      if (defaults?.contactId) body.contactId = defaults.contactId;
      await createTask.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="快速添加任务"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button onClick={submit} loading={submitting}>
            创建
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBox error={error} />
        <Field label="标题" required>
          <Input
            autoFocus
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="要做什么？"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="截止日期">
            <Input type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
          </Field>
          <Field label="截止时间">
            <Input type="time" value={form.dueTime} onChange={(e) => set({ dueTime: e.target.value })} />
          </Field>
          <Field label="优先级">
            <Select value={form.priority} onChange={(e) => set({ priority: e.target.value })}>
              <option value="">默认</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="领域">
            <Select value={form.areaId} onChange={(e) => set({ areaId: e.target.value })}>
              <option value="">未分类</option>
              {(areas ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="备注">
          <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="补充说明（可选）" />
        </Field>
      </div>
    </Modal>
  );
}
